import { useState, useEffect } from 'react';
const validateResponse = (response) => {
    if (!response.ok) {
        return response.text().then((x) => {
            throw new Error(x);
        });
    }
    return response;
};
const validateErrorResponse = (response) => {
    const errorMsg = response.toString().replace('Error:', '').replace(/"/g, '').trim();
    throw new Error(errorMsg);
};
const wrapResponse = (promise) => ({
    then: promise.then.bind(promise),
    json: (output) => promise.then((response) => response.json()).then((value) => (output ? output(value) : value))
});
const getFullUrlWithParams = (path, params) => {
    let url = path;
    if (!path.startsWith('http')) {
        const host = document.location.origin;
        url = `${host}/${path}`;
    }
    if (!params) {
        return url;
    }
    const transform = (value) => {
        if (value === null || value === undefined) {
            return '';
        }
        if (value instanceof Date) {
            return value.toISOString();
        }
        return value.toString();
    };
    const buildParams = () => {
        const p = new URLSearchParams();
        Object.entries(params).forEach(([name, value]) => {
            if (Array.isArray(value)) {
                if (value.length === 0) {
                    return;
                }
                value.map((item) => p.append(name, transform(item)));
            }
            else {
                if (!value) {
                    return;
                }
                p.append(name, transform(value));
            }
        });
        return p;
    };
    const searchParams = buildParams();
    return `${url}?${searchParams}`;
};
const callApi = (method, path, options = {}) => {
    const headers = {
        Accept: 'application/json',
        Credentials: 'same-origin',
        ...options.extraHeaders
    };
    if (options.json) {
        headers['Content-Type'] = 'application/json;charset=UTF-8';
    }
    const request = {
        method,
        body: options.json ? JSON.stringify(options.json) : options.body,
        headers,
        signal: options.signal
    };
    const promise = fetch(getFullUrlWithParams(path, options.params), request).then(validateResponse).catch(validateErrorResponse);
    return wrapResponse(promise);
};
const apiClient = {
    get: (path, options) => callApi('GET', path, options),
    post: (path, options) => callApi('POST', path, options),
    put: (path, options) => callApi('PUT', path, options),
    delete: (path, options) => callApi('DELETE', path, options)
};
export const useApi = () => {
    const [loading, setLoading] = useState(true);
    return { ...apiClient, loading, setLoading };
};
export const usePaginatedApi = (path, options) => {
    var _a, _b;
    const [currentPage, setCurrentPage] = useState(options.initialPage);
    const [response, setResponse] = useState(undefined);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(undefined);
    const request = (optionsToSend) => {
        setLoading(true);
        setError(undefined);
        return apiClient
            .get(path, optionsToSend)
            .json((data) => {
            var _a;
            setResponse(data);
            setCurrentPage(Number((_a = optionsToSend === null || optionsToSend === void 0 ? void 0 : optionsToSend.params) === null || _a === void 0 ? void 0 : _a.currentPage) || 1);
            return data;
        })
            .catch((err) => setError(err))
            .finally(() => setLoading(false));
    };
    useEffect(() => {
        options.params = { ...options.params, pageSize: options.pageSize, currentPage };
        (options === null || options === void 0 ? void 0 : options.fetchOnLoad) && request(options);
    }, []);
    const updateCurrentPage = (newCurrentPage) => {
        if (newCurrentPage < 1 || newCurrentPage > (response === null || response === void 0 ? void 0 : response.totalPages)) {
            return;
        }
        setCurrentPage(newCurrentPage);
        options.params = { ...options.params, pageSize: options.pageSize, currentPage: newCurrentPage };
        request(options);
    };
    const totalPages = (_a = response === null || response === void 0 ? void 0 : response.totalPages) !== null && _a !== void 0 ? _a : 0;
    const totalCount = (_b = response === null || response === void 0 ? void 0 : response.totalCount) !== null && _b !== void 0 ? _b : 0;
    return {
        data: response === null || response === void 0 ? void 0 : response.data,
        loading,
        error,
        totalPages,
        currentPage,
        totalCount,
        prevPage: () => updateCurrentPage(currentPage - 1),
        nextPage: () => updateCurrentPage(currentPage + 1),
        setPage: (newCurrentPage) => updateCurrentPage(newCurrentPage),
        request: (optionsToSend) => request(optionsToSend)
    };
};
